// src/components/coordinateGrid/EquationRenderer.tsx
/**
 * EquationRenderer Component
 * 
 * Renders equations as SVG paths on the coordinate grid.
 * Supports linear and quadratic equations with intercepts and labels.
 * 
 * Phase 2: Graphing Equations
 */

import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../types/redux';
import { selectVisibleEquations, selectEquationIntersections, selectEquation, selectSelectedEquations } from '../../store/slices/equationsSlice';
import { sampleEquation, findIntercepts, evaluateY as evalY, parseEquation } from '../../lib/coordinateGrid/equationParser';
import { Point2D } from '../../types/coordinateGrid';
import { GradientTriangleRenderer } from './GradientTriangleRenderer';

interface EquationRendererProps {
  /** Canvas dimensions */
  width: number;
  height: number;
  /** Viewport transform */
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
  /** Dark mode */
  isDark?: boolean;
}

/**
 * Convert math coordinates to screen coordinates
 */
function mathToScreen(
  point: Point2D,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number
): { x: number; y: number } {
  return {
    x: offsetX + point.x * scaleX,
    y: offsetY - point.y * scaleY, // Y is inverted in screen coordinates
  };
}

/**
 * Check if a point is within the visible viewport
 */
function isPointVisible(
  point: Point2D,
  width: number,
  height: number,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number
): boolean {
  const screen = mathToScreen(point, scaleX, scaleY, offsetX, offsetY);
  return screen.x >= -100 && screen.x <= width + 100 &&
         screen.y >= -100 && screen.y <= height + 100;
}

export const EquationRenderer: React.FC<EquationRendererProps> = ({
  width,
  height,
  scaleX,
  scaleY,
  offsetX,
  offsetY,
  isDark = false,
}) => {
  const dispatch = useDispatch();
  const equations = useSelector(selectVisibleEquations);
  const intersections = useSelector(selectEquationIntersections);

  // Handle equation selection
  const handleEquationClick = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent grid click handling
    dispatch(selectEquation({ id, ctrlKey: e.ctrlKey || e.metaKey }));
  }, [dispatch]);

  // Calculate visible x range
  const xMin = -offsetX / scaleX;
  const xMax = (width - offsetX) / scaleX;
  const yMin = -(height - offsetY) / scaleY;
  const yMax = offsetY / scaleY;

  // Render equations
  const equationElements = useMemo(() => {
    return equations.map((equation) => {
      const parsed = parseEquation(equation.expression);
      if (!parsed.isValid) return null;

      // Sample points for the equation
      const points = sampleEquation(parsed, xMin, xMax, equation.type === 'quadratic' ? 200 : 2);
      
      if (points.length === 0) return null;

      // Convert to screen coordinates
      const screenPoints = points.map(p => mathToScreen(p, scaleX, scaleY, offsetX, offsetY));

      // Create path string
      let pathData = '';
      if (equation.type === 'vertical') {
        // Vertical line: draw from top to bottom of viewport
        const x = screenPoints[0].x;
        pathData = `M ${x} 0 L ${x} ${height}`;
      } else if (equation.type === 'horizontal') {
        // Horizontal line: draw from left to right of viewport
        const y = screenPoints[0].y;
        pathData = `M 0 ${y} L ${width} ${y}`;
      } else if (equation.type === 'linear' && screenPoints.length >= 2) {
        // Linear: simple line between two points
        const p1 = screenPoints[0];
        const p2 = screenPoints[1];
        // Extend line beyond viewport for better rendering
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const extend = 1000; // Extend far beyond viewport
        const startX = p1.x - (dx / Math.abs(dx || 1)) * extend;
        const startY = p1.y - (dy / Math.abs(dy || 1)) * extend;
        const endX = p2.x + (dx / Math.abs(dx || 1)) * extend;
        const endY = p2.y + (dy / Math.abs(dy || 1)) * extend;
        pathData = `M ${startX} ${startY} L ${endX} ${endY}`;
      } else if (equation.type === 'quadratic' && screenPoints.length > 0) {
        // Quadratic: smooth curve
        pathData = `M ${screenPoints[0].x} ${screenPoints[0].y}`;
        for (let i = 1; i < screenPoints.length; i++) {
          pathData += ` L ${screenPoints[i].x} ${screenPoints[i].y}`;
        }
      }

      // Find intercepts if needed
      const intercepts = equation.showIntercepts ? findIntercepts(parsed) : [];

      // Find label position (prefer a point near the middle-right of visible area)
      const labelX = xMin + (xMax - xMin) * 0.7;
      const labelY = evalY(parsed, labelX);
      const labelScreen = labelY !== null 
        ? mathToScreen({ x: labelX, y: labelY }, scaleX, scaleY, offsetX, offsetY)
        : null;

      return (
        <g 
          key={equation.id} 
          className={`equation-${equation.id}`}
          onClick={(e) => handleEquationClick(equation.id, e)}
          style={{ cursor: 'pointer' }}
        >
          {/* Main equation line/path */}
          <path
            d={pathData}
            stroke={equation.color}
            strokeWidth={equation.thickness + (equation.isSelected ? 2 : 0)}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={equation.isSelected ? 1 : 0.9}
            style={{
              filter: equation.isSelected ? 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.5))' : 'none',
              pointerEvents: 'stroke',
            }}
          />
          
          {/* Invisible wide stroke for easier clicking */}
          <path
            d={pathData}
            stroke="transparent"
            strokeWidth={15}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ pointerEvents: 'stroke' }}
          />

          {/* Intercept markers */}
          {intercepts.map((intercept, idx) => {
            const screen = mathToScreen(intercept.point, scaleX, scaleY, offsetX, offsetY);
            if (!isPointVisible(intercept.point, width, height, scaleX, scaleY, offsetX, offsetY)) {
              return null;
            }
            return (
              <g key={`intercept-${idx}`}>
                <circle
                  cx={screen.x}
                  cy={screen.y}
                  r={5}
                  fill={equation.color}
                  stroke={isDark ? '#fff' : '#000'}
                  strokeWidth={1}
                />
                <text
                  x={screen.x + (intercept.type === 'x' ? 8 : 0)}
                  y={screen.y - (intercept.type === 'y' ? 8 : 0)}
                  fontSize="12"
                  fill={isDark ? '#fff' : '#000'}
                  textAnchor={intercept.type === 'x' ? 'start' : 'middle'}
                >
                  ({intercept.type === 'x' ? intercept.value.toFixed(2) : '0'}, {intercept.type === 'y' ? intercept.value.toFixed(2) : '0'})
                </text>
              </g>
            );
          })}

          {/* Equation label */}
          {equation.showLabel && labelScreen && (
            <text
              x={labelScreen.x + 10}
              y={labelScreen.y - 10}
              fontSize="14"
              fill={equation.color}
              fontWeight="500"
              style={{
                textShadow: isDark 
                  ? '1px 1px 2px rgba(0,0,0,0.8)' 
                  : '1px 1px 2px rgba(255,255,255,0.8)',
              }}
            >
              {equation.expression}
            </text>
          )}

          {/* Gradient triangles */}
          {equation.showGradient && equation.gradientTriangles.map((triangle) => (
            <GradientTriangleRenderer
              key={triangle.id}
              triangle={triangle}
              scaleX={scaleX}
              scaleY={scaleY}
              offsetX={offsetX}
              offsetY={offsetY}
              isDark={isDark}
            />
          ))}
        </g>
      );
    });
  }, [equations, width, height, scaleX, scaleY, offsetX, offsetY, xMin, xMax, isDark]);

  // Render intersection points
  const intersectionElements = useMemo(() => {
    return intersections
      .filter(int => int.visible)
      .map((intersection, idx) => {
        const screen = mathToScreen(intersection.point, scaleX, scaleY, offsetX, offsetY);
        if (!isPointVisible(intersection.point, width, height, scaleX, scaleY, offsetX, offsetY)) {
          return null;
        }
        return (
          <g key={`intersection-${idx}`}>
            <circle
              cx={screen.x}
              cy={screen.y}
              r={6}
              fill="#F59E0B"
              stroke={isDark ? '#fff' : '#000'}
              strokeWidth={2}
            />
            <text
              x={screen.x + 10}
              y={screen.y - 10}
              fontSize="12"
              fill={isDark ? '#fff' : '#000'}
            >
              ({intersection.point.x.toFixed(2)}, {intersection.point.y.toFixed(2)})
            </text>
          </g>
        );
      });
  }, [intersections, width, height, scaleX, scaleY, offsetX, offsetY, isDark]);

  return (
    <g className="equation-layer">
      {equationElements}
      {intersectionElements}
    </g>
  );
};

